/**
 * Observation historique : un chantier (ou un devis) sur lequel une prestation
 * a été réalisée / chiffrée.
 *
 * ─── Modèle de prix ────────────────────────────────────────────────────────
 *
 * Un chiffrage se construit en deux étages :
 *   Niveau LIGNE (par prestation, stable dans le temps) :
 *     (1) Fourniture + (2) Main d'œuvre + (3) Appro  =  PU F+P nu
 *   Niveau AFFAIRE (par chiffrage, conjoncturel — client, projet, année) :
 *     PU F+P nu × (1 + frais chantier) × (1 + FG) × (1 + marge) × coef
 *                                                     =  PU vendu final
 *
 * Pour que la biblio serve de référence technique ré-exploitable, on remonte
 * **uniquement le PU F+P nu** (niveau ligne). Les coefs affaire sont
 * conjoncturels et ne se reproduisent pas dans un futur chiffrage.
 *
 * ─── Champs de prix ────────────────────────────────────────────────────────
 *
 *  - `prixUnitaire`       — **PU F+P nu** (F + MO + appro, sans coefs affaire).
 *                            C'est la référence technique réutilisable pour
 *                            re-chiffrer. Tous les chantiers en biblio
 *                            exposent cette valeur sous cette sémantique.
 *  - `prixVenduHistorique`— optionnel. Prix effectivement facturé au client sur
 *                            le chantier d'origine (marge + FG + frais inclus).
 *                            Sert de benchmark marché (« on a vendu ça X € en
 *                            2024 ») mais n'est pas utilisé comme référence
 *                            pour re-chiffrer.
 *  - `coefAffaireApplique`— optionnel. Trace du coef global affaire appliqué
 *                            au moment de la vente :
 *                            (1+FG) × (1+marge) × (1+frais) × coef ligne.
 *                            Permet de re-dériver prixVenduHistorique ≈
 *                            prixUnitaire × coefAffaireApplique.
 *  - `coutRevient`        — optionnel. Décomposition fine du PU F+P nu en ses
 *                            trois composantes (F / MO / appro), utile pour
 *                            des agrégats ou des suggestions de décomposition.
 */
export interface Chantier {
  id: number;
  nom: string;
  localisation: string;
  annee: string;
  dateChantier: string; // Date au format YYYY-MM-DD pour un tri précis
  fournisseur: string;
  quantite: string;
  /** PU F+P nu — voir docblock. */
  prixUnitaire: string;
  /** PU actualisé par indice BT (même sémantique que prixUnitaire : PU nu). */
  prixActualise?: string;
  prixTotal: string;
  /** Optionnel : prix effectivement facturé au client, benchmark marché. */
  prixVenduHistorique?: string;
  /** Optionnel : coef global affaire appliqué à l'époque. */
  coefAffaireApplique?: number;
  /** Optionnel : décomposition fine du PU nu en F / MO / appro. */
  coutRevient?: {
    fourniture?: number;
    mainOeuvre?: number;
    approvisionnement?: number;
  };
  source?: 'Facture négociée' | 'Facture non négociée' | 'Devis retenu' | 'Devis non retenu';
  description?: string;
}

/**
 * Type d'un ouvrage de la bibliothèque :
 *  - 'F+P' : prestation vendue fourni + posé (fourniture matière + pose) — c'est le cas par défaut
 *    pour la grande majorité des entrées chiffrage (ex : « Cloison BA13 sur ossature »).
 *  - 'F'   : fourniture nue (prix d'achat matière uniquement, sans pose).
 *    Les entrées F peuvent porter des références MO indicatives (temps pose, nb hommes)
 *    pour pré-remplir une décomposition quand la fourniture est utilisée dans une ligne F+P.
 *
 * Règles de filtrage des recherches :
 *  - Recherche côté subbar décomposition (champ fourniture) → F uniquement.
 *  - Recherche côté searchbar principale du chiffrage      → F+P uniquement.
 *  - Recherche côté ligne ventilée                          → F+P uniquement.
 */
export type OuvrageType = 'F' | 'F+P';

export interface Ouvrage {
  id: number;
  type: OuvrageType;
  intitule: string;
  categorie: string;
  unite: string;
  chantiers: Chantier[];
  // Références Fourniture / Main d'œuvre pour pré-remplir une ligne ventilée
  // ou une décomposition F/MO. Optionnels et non destructifs : les ouvrages
  // existants restent valides sans ces champs.
  coutFournitureRef?: number;  // €/unité
  tempsPoseRef?: number;       // h/unité
  nbHommesRef?: number;        // taille d'équipe
}

// Données fictives de démonstration pour la bibliothèque de prix
export const allData: Ouvrage[] = [
  {
    id: 1,
    type: 'F+P',
    intitule: "Cloison BA13 sur ossature métallique",
    categorie: "Second œuvre > Plâtrerie",
    unite: "m²",
    chantiers: [
      {
        id: 101,
        nom: "Résidence Les Jardins",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-01-15",
        fournisseur: "Saint-Gobain",
        quantite: "150 m²",
        prixUnitaire: "28.50 €/m²",
        prixActualise: "29.80 €/m²",
        prixTotal: "4 275 €",
        source: "Facture négociée",
        description: "Cloison BA13 13mm sur ossature métallique 48mm, fourniture et pose"
      },
      {
        id: 102,
        nom: "Centre commercial Lyon",
        localisation: "Lyon",
        annee: "2023",
        dateChantier: "2023-11-20",
        fournisseur: "Knauf",
        quantite: "200 m²",
        prixUnitaire: "26.80 €/m²",
        prixActualise: "28.20 €/m²",
        prixTotal: "5 360 €",
        source: "Devis retenu",
        description: "Cloison BA13 standard, pose sèche"
      },
      {
        id: 103,
        nom: "Bureaux modernes",
        localisation: "Marseille",
        annee: "2023",
        dateChantier: "2023-08-10",
        fournisseur: "Placo",
        quantite: "80 m²",
        prixUnitaire: "27.20 €/m²",
        prixActualise: "28.90 €/m²",
        prixTotal: "2 176 €",
        source: "Devis non retenu",
        description: "Cloison BA13 avec isolation phonique"
      }
    ]
  },
  {
    id: 2,
    type: 'F+P',
    intitule: "Béton armé C25/30",
    categorie: "Gros œuvre > Structure",
    unite: "m³",
    chantiers: [
      {
        id: 201,
        nom: "Résidence Les Jardins",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-02-01",
        fournisseur: "Lafarge",
        quantite: "150 m³",
        prixUnitaire: "85.00 €/m³",
        prixActualise: "88.90 €/m³",
        prixTotal: "12 750 €",
        source: "Facture négociée",
        description: "Béton armé classe C25/30, livraison par camion toupie"
      },
      {
        id: 202,
        nom: "Centre commercial Lyon",
        localisation: "Lyon",
        annee: "2023",
        dateChantier: "2023-12-15",
        fournisseur: "Vicat",
        quantite: "200 m³",
        prixUnitaire: "82.50 €/m³",
        prixActualise: "86.80 €/m³",
        prixTotal: "16 500 €",
        source: "Devis retenu",
        description: "Béton armé pour structure commerciale"
      },
      {
        id: 203,
        nom: "Bureaux modernes",
        localisation: "Marseille",
        annee: "2023",
        dateChantier: "2023-09-20",
        fournisseur: "Cemex",
        quantite: "120 m³",
        prixUnitaire: "83.20 €/m³",
        prixActualise: "88.20 €/m³",
        prixTotal: "9 984 €",
        source: "Facture non négociée",
        description: "Béton armé avec adjuvant plastifiant"
      }
    ]
  },
  {
    id: 3,
    type: 'F+P',
    intitule: "Pose de carrelage 20x20",
    categorie: "Second œuvre > Carrelage",
    unite: "m²",
    chantiers: [
      {
        id: 301,
        nom: "Centre commercial Lyon",
        localisation: "Lyon",
        annee: "2024",
        dateChantier: "2024-01-20",
        fournisseur: "Saint-Gobain",
        quantite: "80 m²",
        prixUnitaire: "45.00 €/m²",
        prixActualise: "47.10 €/m²",
        prixTotal: "3 600 €",
        source: "Facture négociée",
        description: "Carrelage grès cérame 20x20 cm, pose collée, joint de 2mm"
      },
      {
        id: 302,
        nom: "Appartements neufs",
        localisation: "Marseille",
        annee: "2024",
        dateChantier: "2023-11-15",
        fournisseur: "Ceramiche Refin",
        quantite: "120 m²",
        prixUnitaire: "48.00 €/m²",
        prixActualise: "50.50 €/m²",
        prixTotal: "5 760 €",
        source: "Devis retenu",
        description: "Carrelage design 20x20 cm, pose avec colle flexible"
      },
      {
        id: 303,
        nom: "Bureaux modernes",
        localisation: "Marseille",
        annee: "2023",
        dateChantier: "2023-08-05",
        fournisseur: "Marazzi",
        quantite: "60 m²",
        prixUnitaire: "42.50 €/m²",
        prixActualise: "45.10 €/m²",
        prixTotal: "2 550 €",
        source: "Devis non retenu",
        description: "Carrelage technique 20x20 cm"
      }
    ]
  },
  {
    id: 4,
    type: 'F+P',
    intitule: "Installation électrique complète",
    categorie: "Technique > Électricité",
    unite: "m²",
    chantiers: [
      {
        id: 401,
        nom: "Bureaux modernes",
        localisation: "Marseille",
        annee: "2023",
        dateChantier: "2023-12-01",
        fournisseur: "Schneider Electric",
        quantite: "500 m²",
        prixUnitaire: "120.00 €/m²",
        prixActualise: "126.20 €/m²",
        prixTotal: "60 000 €",
        source: "Facture négociée",
        description: "Installation électrique complète : tableau, prises, éclairage, câblage"
      },
      {
        id: 402,
        nom: "Centre commercial Lyon",
        localisation: "Lyon",
        annee: "2023",
        dateChantier: "2023-09-15",
        fournisseur: "Legrand",
        quantite: "800 m²",
        prixUnitaire: "115.00 €/m²",
        prixActualise: "121.80 €/m²",
        prixTotal: "92 000 €",
        source: "Devis retenu",
        description: "Installation électrique tertiaire avec domotique"
      }
    ]
  },
  {
    id: 5,
    type: 'F+P',
    intitule: "Plomberie sanitaire",
    categorie: "Technique > Plomberie",
    unite: "forfait",
    chantiers: [
      {
        id: 501,
        nom: "Appartements neufs",
        localisation: "Marseille",
        annee: "2024",
        dateChantier: "2024-01-10",
        fournisseur: "Grohe",
        quantite: "24",
        prixUnitaire: "2 500 €/ff",
        prixActualise: "2 615 €/ff",
        prixTotal: "60 000 €",
        source: "Facture négociée",
        description: "Installation complète plomberie sanitaire : éviers, douches, WC, robinetterie"
      },
      {
        id: 502,
        nom: "Résidence Les Jardins",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2023-10-20",
        fournisseur: "Hansgrohe",
        quantite: "18",
        prixUnitaire: "2 400 €/ff",
        prixActualise: "2 540 €/ff",
        prixTotal: "43 200 €",
        source: "Devis retenu",
        description: "Plomberie sanitaire avec robinetterie design"
      }
    ]
  },
  {
    id: 6,
    type: 'F+P',
    intitule: "Charpente traditionnelle",
    categorie: "Gros œuvre > Charpente",
    unite: "m²",
    chantiers: [
      {
        id: 601,
        nom: "Maison individuelle",
        localisation: "Nantes",
        annee: "2024",
        dateChantier: "2024-01-05",
        fournisseur: "Charpente du Nord",
        quantite: "120 m²",
        prixUnitaire: "95.00 €/m²",
        prixActualise: "98.50 €/m²",
        prixTotal: "11 400 €",
        source: "Devis retenu",
        description: "Charpente traditionnelle en chêne massif, assemblage à tenon-mortaise"
      },
      {
        id: 602,
        nom: "Gîte rural",
        localisation: "Bretagne",
        annee: "2023",
        dateChantier: "2023-10-15",
        fournisseur: "Bois du Jura",
        quantite: "80 m²",
        prixUnitaire: "92.50 €/m²",
        prixActualise: "96.80 €/m²",
        prixTotal: "7 400 €",
        source: "Facture négociée",
        description: "Charpente traditionnelle en sapin du Nord"
      }
    ]
  },
  {
    id: 7,
    type: 'F+P',
    intitule: "Couverture en tuiles",
    categorie: "Gros œuvre > Couverture",
    unite: "m²",
    chantiers: [
      {
        id: 701,
        nom: "Résidence Les Jardins",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-01-20",
        fournisseur: "Tuilerie de la Marne",
        quantite: "200 m²",
        prixUnitaire: "65.00 €/m²",
        prixActualise: "68.20 €/m²",
        prixTotal: "13 000 €",
        source: "Facture négociée",
        description: "Tuiles terre cuite 30x40 cm, pose sur volige, isolation thermique incluse"
      },
      {
        id: 702,
        nom: "Maison individuelle",
        localisation: "Nantes",
        annee: "2023",
        dateChantier: "2023-11-10",
        fournisseur: "Imerys",
        quantite: "150 m²",
        prixUnitaire: "62.50 €/m²",
        prixActualise: "65.80 €/m²",
        prixTotal: "9 375 €",
        source: "Devis retenu",
        description: "Tuiles terre cuite traditionnelles, pose sur liteaux"
      }
    ]
  },
  {
    id: 8,
    type: 'F+P',
    intitule: "Isolation thermique combles",
    categorie: "Second œuvre > Isolation",
    unite: "m²",
    chantiers: [
      {
        id: 801,
        nom: "Résidence Les Jardins",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-02-10",
        fournisseur: "Isover",
        quantite: "300 m²",
        prixUnitaire: "18.50 €/m²",
        prixActualise: "19.40 €/m²",
        prixTotal: "5 550 €",
        source: "Facture négociée",
        description: "Isolation en laine de verre R=6.0, pare-vapeur, pose sur combles perdus"
      },
      {
        id: 802,
        nom: "Centre commercial Lyon",
        localisation: "Lyon",
        annee: "2023",
        dateChantier: "2023-12-20",
        fournisseur: "Knauf",
        quantite: "500 m²",
        prixUnitaire: "17.80 €/m²",
        prixActualise: "18.90 €/m²",
        prixTotal: "8 900 €",
        source: "Devis retenu",
        description: "Isolation en laine minérale R=5.5, pare-vapeur haute performance"
      }
    ]
  },
  {
    id: 9,
    type: 'F+P',
    intitule: "Menuiserie extérieure PVC",
    categorie: "Second œuvre > Menuiserie",
    unite: "m²",
    chantiers: [
      {
        id: 901,
        nom: "Appartements neufs",
        localisation: "Marseille",
        annee: "2024",
        dateChantier: "2024-01-25",
        fournisseur: "Veka",
        quantite: "180 m²",
        prixUnitaire: "320.00 €/m²",
        prixActualise: "335.20 €/m²",
        prixTotal: "57 600 €",
        source: "Facture négociée",
        description: "Fenêtres PVC double vitrage 4/16/4, volets roulants intégrés"
      },
      {
        id: 902,
        nom: "Bureaux modernes",
        localisation: "Marseille",
        annee: "2023",
        dateChantier: "2023-09-25",
        fournisseur: "Rehau",
        quantite: "120 m²",
        prixUnitaire: "315.00 €/m²",
        prixActualise: "330.80 €/m²",
        prixTotal: "37 800 €",
        source: "Devis retenu",
        description: "Fenêtres PVC triple vitrage, isolation thermique renforcée"
      }
    ]
  },
  {
    id: 10,
    type: 'F+P',
    intitule: "Peinture intérieure",
    categorie: "Second œuvre > Peinture",
    unite: "m²",
    chantiers: [
      {
        id: 1001,
        nom: "Centre commercial Lyon",
        localisation: "Lyon",
        annee: "2024",
        dateChantier: "2024-01-30",
        fournisseur: "Dulux",
        quantite: "800 m²",
        prixUnitaire: "12.50 €/m²",
        prixActualise: "13.10 €/m²",
        prixTotal: "10 000 €",
        source: "Facture négociée",
        description: "Peinture acrylique mate, 2 couches, préparation des supports incluse"
      },
      {
        id: 1002,
        nom: "Bureaux modernes",
        localisation: "Marseille",
        annee: "2023",
        dateChantier: "2023-10-30",
        fournisseur: "Sikkens",
        quantite: "400 m²",
        prixUnitaire: "13.20 €/m²",
        prixActualise: "13.90 €/m²",
        prixTotal: "5 280 €",
        source: "Devis retenu",
        description: "Peinture professionnelle, finition satinée, protection anti-bactérienne"
      }
    ]
  },
  // -----------------------------------------------------------------------
  // Fournitures nues (F) — paysagisme / végétalisation
  // Données de référence fourniture + temps de pose indicatifs pour pré-remplir
  // une décomposition F/MO lorsqu'utilisées dans une ligne F+P.
  // -----------------------------------------------------------------------
  {
    id: 11,
    type: 'F',
    intitule: "Jardinières métalliques",
    categorie: "Aménagement extérieur > Mobilier",
    unite: "u",
    coutFournitureRef: 1000,
    tempsPoseRef: 0.25,
    nbHommesRef: 2,
    chantiers: [
      {
        id: 1101,
        nom: "Toiture végétalisée Hôtel Mercure",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-02-10",
        fournisseur: "Atelier So Green",
        quantite: "12 u",
        prixUnitaire: "1 000.00 €/u",
        prixActualise: "1 045.00 €/u",
        prixTotal: "12 000 €",
        source: "Facture négociée",
        description: "Jardinière acier corten sur mesure, fourniture nue (hors pose)"
      }
    ]
  },
  {
    id: 12,
    type: 'F',
    intitule: "Drainage en bille d'argile",
    categorie: "Aménagement extérieur > Substrat",
    unite: "m²",
    coutFournitureRef: 9,
    tempsPoseRef: 0.04,
    nbHommesRef: 1,
    chantiers: [
      {
        id: 1201,
        nom: "Toiture végétalisée Hôtel Mercure",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-02-12",
        fournisseur: "Laterlite",
        quantite: "180 m²",
        prixUnitaire: "9.00 €/m²",
        prixActualise: "9.40 €/m²",
        prixTotal: "1 620 €",
        source: "Facture négociée",
        description: "Bille d'argile expansée pour couche drainante, fourniture en vrac"
      }
    ]
  },
  {
    id: 13,
    type: 'F',
    intitule: "Nappe filtrante",
    categorie: "Aménagement extérieur > Substrat",
    unite: "m²",
    coutFournitureRef: 1,
    tempsPoseRef: 0.02,
    nbHommesRef: 2,
    chantiers: [
      {
        id: 1301,
        nom: "Toiture végétalisée Hôtel Mercure",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-02-12",
        fournisseur: "Soprema",
        quantite: "180 m²",
        prixUnitaire: "1.00 €/m²",
        prixActualise: "1.05 €/m²",
        prixTotal: "180 €",
        source: "Facture négociée",
        description: "Géotextile filtrant pour séparation drainage / substrat"
      }
    ]
  },
  {
    id: 14,
    type: 'F',
    intitule: "Substrat intensif allégé (65 cm)",
    categorie: "Aménagement extérieur > Substrat",
    unite: "m³",
    coutFournitureRef: 120,
    tempsPoseRef: 0.5,
    nbHommesRef: 1,
    chantiers: [
      {
        id: 1401,
        nom: "Toiture végétalisée Hôtel Mercure",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-02-14",
        fournisseur: "Florentaise",
        quantite: "117 m³",
        prixUnitaire: "120.00 €/m³",
        prixActualise: "125.40 €/m³",
        prixTotal: "14 040 €",
        source: "Facture négociée",
        description: "Substrat intensif allégé spécifique toiture-terrasse, épaisseur 65 cm"
      }
    ]
  },
  {
    id: 15,
    type: 'F',
    intitule: "Sujets structurants",
    categorie: "Aménagement extérieur > Végétaux",
    unite: "u",
    coutFournitureRef: 15,
    tempsPoseRef: 0.08,
    nbHommesRef: 2,
    chantiers: [
      {
        id: 1501,
        nom: "Toiture végétalisée Hôtel Mercure",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-02-20",
        fournisseur: "Pépinières Lemonnier",
        quantite: "45 u",
        prixUnitaire: "15.00 €/u",
        prixActualise: "15.70 €/u",
        prixTotal: "675 €",
        source: "Facture négociée",
        description: "Sujets structurants (arbustes moyens, conteneur 5 L)"
      }
    ]
  },
  {
    id: 16,
    type: 'F',
    intitule: "Graminées et vivaces (6 u/m²)",
    categorie: "Aménagement extérieur > Végétaux",
    unite: "u",
    coutFournitureRef: 3,
    tempsPoseRef: 0.015,
    nbHommesRef: 1,
    chantiers: [
      {
        id: 1601,
        nom: "Toiture végétalisée Hôtel Mercure",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-02-22",
        fournisseur: "Pépinières Lemonnier",
        quantite: "1 080 u",
        prixUnitaire: "3.00 €/u",
        prixActualise: "3.15 €/u",
        prixTotal: "3 240 €",
        source: "Facture négociée",
        description: "Graminées et vivaces, densité 6 u/m², godet 9 cm"
      }
    ]
  },
  {
    id: 17,
    type: 'F',
    intitule: "Pots (drainage + substrat + plante)",
    categorie: "Aménagement extérieur > Mobilier",
    unite: "u",
    coutFournitureRef: 400,
    tempsPoseRef: 0.75,
    nbHommesRef: 1,
    chantiers: [
      {
        id: 1701,
        nom: "Aménagement patio Résidence Rivoli",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-03-05",
        fournisseur: "Image'In Jardin",
        quantite: "20 u",
        prixUnitaire: "400.00 €/u",
        prixActualise: "418.00 €/u",
        prixTotal: "8 000 €",
        source: "Facture négociée",
        description: "Pot décoratif complet (contenant + drainage + substrat + sujet végétal)"
      }
    ]
  },
  // -----------------------------------------------------------------------
  // Prestations vendues fourni + posé (F+P) — paysagisme / végétalisation
  // Variantes de prix sur plusieurs chantiers : permettent à l'utilisateur de
  // partir d'un PU benchmark plutôt que d'une décomposition F + MO.
  // -----------------------------------------------------------------------
  {
    id: 18,
    type: 'F+P',
    intitule: "Fourniture et pose jardinières métalliques",
    categorie: "Aménagement extérieur > Mobilier",
    unite: "u",
    chantiers: [
      {
        id: 1801,
        nom: "Toiture végétalisée Hôtel Mercure",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-02-10",
        fournisseur: "Atelier So Green",
        quantite: "12 u",
        prixUnitaire: "1 290.00 €/u",
        prixActualise: "1 348.00 €/u",
        prixTotal: "15 480 €",
        source: "Facture négociée",
        description: "Jardinière acier corten sur mesure, fourniture + pose (incl. ancrage et étanchéité)"
      },
      {
        id: 1802,
        nom: "Jardin suspendu Lutetia",
        localisation: "Paris",
        annee: "2025",
        dateChantier: "2025-04-22",
        fournisseur: "Métal & Végétal",
        quantite: "8 u",
        prixUnitaire: "1 380.00 €/u",
        prixActualise: "1 380.00 €/u",
        prixTotal: "11 040 €",
        source: "Devis retenu",
        description: "Jardinière acier corten finition vieillie, pose en hauteur avec sécurisation"
      },
      {
        id: 1803,
        nom: "Espaces verts Bureaux Part-Dieu",
        localisation: "Lyon",
        annee: "2023",
        dateChantier: "2023-09-18",
        fournisseur: "Atelier So Green",
        quantite: "16 u",
        prixUnitaire: "1 230.00 €/u",
        prixActualise: "1 305.00 €/u",
        prixTotal: "19 680 €",
        source: "Facture négociée",
        description: "Jardinière acier galvanisé standard, pose en pleine terre"
      }
    ]
  },
  {
    id: 19,
    type: 'F+P',
    intitule: "Fourniture et pose drainage bille d'argile",
    categorie: "Aménagement extérieur > Substrat",
    unite: "m²",
    chantiers: [
      {
        id: 1901,
        nom: "Toiture végétalisée Hôtel Mercure",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-02-12",
        fournisseur: "Laterlite",
        quantite: "180 m²",
        prixUnitaire: "14.50 €/m²",
        prixActualise: "15.15 €/m²",
        prixTotal: "2 610 €",
        source: "Facture négociée",
        description: "Bille d'argile expansée 8 cm, fourniture + étalement"
      },
      {
        id: 1902,
        nom: "Toiture végétalisée Office Tower La Défense",
        localisation: "Courbevoie",
        annee: "2025",
        dateChantier: "2025-03-08",
        fournisseur: "Soprema",
        quantite: "420 m²",
        prixUnitaire: "16.80 €/m²",
        prixActualise: "16.80 €/m²",
        prixTotal: "7 056 €",
        source: "Devis retenu",
        description: "Bille d'argile expansée 10 cm, accès grue, contraintes hauteur"
      },
      {
        id: 1903,
        nom: "Aménagement Campus Bordeaux",
        localisation: "Bordeaux",
        annee: "2024",
        dateChantier: "2024-06-14",
        fournisseur: "Laterlite",
        quantite: "260 m²",
        prixUnitaire: "13.90 €/m²",
        prixActualise: "14.55 €/m²",
        prixTotal: "3 614 €",
        source: "Facture négociée",
        description: "Bille d'argile expansée 8 cm, étalement mécanique en plein sol"
      }
    ]
  },
  {
    id: 20,
    type: 'F+P',
    intitule: "Fourniture et pose nappe filtrante",
    categorie: "Aménagement extérieur > Substrat",
    unite: "m²",
    chantiers: [
      {
        id: 2001,
        nom: "Toiture végétalisée Hôtel Mercure",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-02-12",
        fournisseur: "Soprema",
        quantite: "180 m²",
        prixUnitaire: "4.20 €/m²",
        prixActualise: "4.40 €/m²",
        prixTotal: "756 €",
        source: "Facture négociée",
        description: "Géotextile filtrant 200 g/m², pose avec recouvrement 15 cm"
      },
      {
        id: 2002,
        nom: "Toiture végétalisée Office Tower La Défense",
        localisation: "Courbevoie",
        annee: "2025",
        dateChantier: "2025-03-08",
        fournisseur: "Texinov",
        quantite: "420 m²",
        prixUnitaire: "5.10 €/m²",
        prixActualise: "5.10 €/m²",
        prixTotal: "2 142 €",
        source: "Devis retenu",
        description: "Géotextile filtrant renforcé 250 g/m², pose en hauteur"
      },
      {
        id: 2003,
        nom: "Jardin suspendu Lutetia",
        localisation: "Paris",
        annee: "2025",
        dateChantier: "2025-04-25",
        fournisseur: "Soprema",
        quantite: "95 m²",
        prixUnitaire: "4.60 €/m²",
        prixActualise: "4.60 €/m²",
        prixTotal: "437 €",
        source: "Devis retenu",
        description: "Géotextile filtrant 200 g/m², pose soignée en zone visible"
      }
    ]
  },
  {
    id: 21,
    type: 'F+P',
    intitule: "Fourniture et pose substrat intensif allégé (65 cm)",
    categorie: "Aménagement extérieur > Substrat",
    unite: "m³",
    chantiers: [
      {
        id: 2101,
        nom: "Toiture végétalisée Hôtel Mercure",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-02-14",
        fournisseur: "Florentaise",
        quantite: "117 m³",
        prixUnitaire: "182.00 €/m³",
        prixActualise: "190.20 €/m³",
        prixTotal: "21 294 €",
        source: "Facture négociée",
        description: "Substrat intensif allégé toiture-terrasse, étalement et profilage"
      },
      {
        id: 2102,
        nom: "Toiture végétalisée Office Tower La Défense",
        localisation: "Courbevoie",
        annee: "2025",
        dateChantier: "2025-03-12",
        fournisseur: "Florentaise",
        quantite: "275 m³",
        prixUnitaire: "210.00 €/m³",
        prixActualise: "210.00 €/m³",
        prixTotal: "57 750 €",
        source: "Devis retenu",
        description: "Substrat intensif allégé en hauteur, contraintes logistique grue"
      },
      {
        id: 2103,
        nom: "Aménagement Campus Bordeaux",
        localisation: "Bordeaux",
        annee: "2024",
        dateChantier: "2024-06-18",
        fournisseur: "Terreaux du Sud-Ouest",
        quantite: "165 m³",
        prixUnitaire: "175.00 €/m³",
        prixActualise: "183.00 €/m³",
        prixTotal: "28 875 €",
        source: "Facture négociée",
        description: "Substrat intensif allégé, étalement mécanique en pleine terre"
      }
    ]
  },
  {
    id: 22,
    type: 'F+P',
    intitule: "Fourniture et plantation sujets structurants",
    categorie: "Aménagement extérieur > Végétaux",
    unite: "u",
    chantiers: [
      {
        id: 2201,
        nom: "Toiture végétalisée Hôtel Mercure",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-02-20",
        fournisseur: "Pépinières Lemonnier",
        quantite: "45 u",
        prixUnitaire: "32.00 €/u",
        prixActualise: "33.40 €/u",
        prixTotal: "1 440 €",
        source: "Facture négociée",
        description: "Arbustes persistants conteneur 5 L, plantation soignée"
      },
      {
        id: 2202,
        nom: "Jardin suspendu Lutetia",
        localisation: "Paris",
        annee: "2025",
        dateChantier: "2025-04-28",
        fournisseur: "Pépinières du Bocage",
        quantite: "30 u",
        prixUnitaire: "38.00 €/u",
        prixActualise: "38.00 €/u",
        prixTotal: "1 140 €",
        source: "Devis retenu",
        description: "Arbustes structurants conteneur 7 L, plantation en pots haute qualité"
      },
      {
        id: 2203,
        nom: "Espaces verts Bureaux Part-Dieu",
        localisation: "Lyon",
        annee: "2023",
        dateChantier: "2023-10-05",
        fournisseur: "Pépinières Lemonnier",
        quantite: "70 u",
        prixUnitaire: "28.50 €/u",
        prixActualise: "30.20 €/u",
        prixTotal: "1 995 €",
        source: "Facture négociée",
        description: "Arbustes persistants conteneur 5 L, plantation pleine terre"
      }
    ]
  },
  {
    id: 23,
    type: 'F+P',
    intitule: "Fourniture et plantation graminées et vivaces (6 u/m²)",
    categorie: "Aménagement extérieur > Végétaux",
    unite: "u",
    chantiers: [
      {
        id: 2301,
        nom: "Toiture végétalisée Hôtel Mercure",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-02-22",
        fournisseur: "Pépinières Lemonnier",
        quantite: "1 080 u",
        prixUnitaire: "5.20 €/u",
        prixActualise: "5.43 €/u",
        prixTotal: "5 616 €",
        source: "Facture négociée",
        description: "Graminées et vivaces godet 9 cm, densité 6 u/m², plantation manuelle"
      },
      {
        id: 2302,
        nom: "Aménagement Campus Bordeaux",
        localisation: "Bordeaux",
        annee: "2024",
        dateChantier: "2024-06-22",
        fournisseur: "Pépinières Aquitaines",
        quantite: "1 560 u",
        prixUnitaire: "4.80 €/u",
        prixActualise: "5.02 €/u",
        prixTotal: "7 488 €",
        source: "Facture négociée",
        description: "Graminées et vivaces godet 9 cm, plantation au cordeau"
      },
      {
        id: 2303,
        nom: "Toiture végétalisée Office Tower La Défense",
        localisation: "Courbevoie",
        annee: "2025",
        dateChantier: "2025-03-18",
        fournisseur: "Pépinières du Bocage",
        quantite: "2 520 u",
        prixUnitaire: "5.80 €/u",
        prixActualise: "5.80 €/u",
        prixTotal: "14 616 €",
        source: "Devis retenu",
        description: "Graminées et vivaces godet 9 cm, plantation en hauteur"
      }
    ]
  },
  {
    id: 24,
    type: 'F+P',
    intitule: "Fourniture et pose pots décoratifs complets",
    categorie: "Aménagement extérieur > Mobilier",
    unite: "u",
    chantiers: [
      {
        id: 2401,
        nom: "Aménagement patio Résidence Rivoli",
        localisation: "Paris",
        annee: "2024",
        dateChantier: "2024-03-05",
        fournisseur: "Image'In Jardin",
        quantite: "20 u",
        prixUnitaire: "510.00 €/u",
        prixActualise: "532.95 €/u",
        prixTotal: "10 200 €",
        source: "Facture négociée",
        description: "Pot décoratif complet (contenant + drainage + substrat + plante), pose simple"
      },
      {
        id: 2402,
        nom: "Patio Résidence Saint-Honoré",
        localisation: "Paris",
        annee: "2023",
        dateChantier: "2023-11-12",
        fournisseur: "Image'In Jardin",
        quantite: "14 u",
        prixUnitaire: "495.00 €/u",
        prixActualise: "524.70 €/u",
        prixTotal: "6 930 €",
        source: "Facture négociée",
        description: "Pot décoratif standard, plantation arbuste persistant"
      },
      {
        id: 2403,
        nom: "Jardin suspendu Lutetia",
        localisation: "Paris",
        annee: "2025",
        dateChantier: "2025-04-30",
        fournisseur: "Métal & Végétal",
        quantite: "8 u",
        prixUnitaire: "580.00 €/u",
        prixActualise: "580.00 €/u",
        prixTotal: "4 640 €",
        source: "Devis retenu",
        description: "Pot décoratif gamme premium, plante structurante installée"
      }
    ]
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Gros œuvre — chantier de référence « Résidence Les Tilleuls »
  // (migrés depuis 3-chiffrage/data/defaultBlocks.ts pour devenir cherchables
  // depuis la barre principale, la barre de prestation et la ligne ventilée)
  // ─────────────────────────────────────────────────────────────────────────

  // ── Préparation chantier ──
  {
    id: 25,
    type: 'F+P',
    intitule: "Amenée / repli",
    categorie: "Gros œuvre > Préparation",
    unite: "ens",
    chantiers: [
      {
        id: 2501,
        nom: "Résidence Les Tilleuls",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-02-10",
        fournisseur: "BTP Renard",
        quantite: "1 ens",
        prixUnitaire: "2 500.00 €/ens",
        prixActualise: "2 500.00 €/ens",
        prixTotal: "2 500 €",
        source: "Devis retenu",
        description: "Amenée et repli des moyens de chantier (forfait)"
      }
    ]
  },
  {
    id: 26,
    type: 'F+P',
    intitule: "Implantation des ouvrages par géomètre",
    categorie: "Gros œuvre > Préparation",
    unite: "U",
    chantiers: [
      {
        id: 2601,
        nom: "Résidence Les Tilleuls",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-02-12",
        fournisseur: "VRD Experts",
        quantite: "107 U",
        prixUnitaire: "25.00 €/U",
        prixActualise: "25.00 €/U",
        prixTotal: "2 675 €",
        source: "Devis retenu",
        description: "Implantation des ouvrages par géomètre"
      }
    ]
  },
  {
    id: 27,
    type: 'F+P',
    intitule: "Gestion de la nappe et des eaux (rabattement, pompage)",
    categorie: "Gros œuvre > Préparation",
    unite: "ens",
    chantiers: [
      {
        id: 2701,
        nom: "Résidence Les Tilleuls",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-02-15",
        fournisseur: "VRD Experts",
        quantite: "1 ens",
        prixUnitaire: "2 000.00 €/ens",
        prixActualise: "2 000.00 €/ens",
        prixTotal: "2 000 €",
        source: "Devis retenu",
        description: "Rabattement de nappe et pompage des eaux"
      }
    ]
  },

  // ── Terrassement — Fouilles ──
  {
    id: 28,
    type: 'F+P',
    intitule: "Fouilles en trous et rigoles pour fondations",
    categorie: "Gros œuvre > Terrassement",
    unite: "m³",
    chantiers: [
      {
        id: 2801,
        nom: "Résidence Les Tilleuls",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-02-20",
        fournisseur: "VRD Experts",
        quantite: "72 m³",
        prixUnitaire: "34.45 €/m³",
        prixActualise: "34.45 €/m³",
        prixTotal: "2 480 €",
        source: "Devis retenu",
        description: "Fouilles en trous et en rigoles pour fondations"
      }
    ]
  },
  {
    id: 29,
    type: 'F+P',
    intitule: "Fouilles rigoles pour réseaux",
    categorie: "Gros œuvre > Terrassement",
    unite: "m³",
    chantiers: [
      {
        id: 2901,
        nom: "Résidence Les Tilleuls",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-02-22",
        fournisseur: "VRD Experts",
        quantite: "63 m³",
        prixUnitaire: "34.45 €/m³",
        prixActualise: "34.45 €/m³",
        prixTotal: "2 170 €",
        source: "Devis retenu",
        description: "Fouilles en rigoles pour passage des réseaux enterrés"
      }
    ]
  },

  // ── Terrassement — Remblais ──
  {
    id: 30,
    type: 'F+P',
    intitule: "Remblais des trous et des rigoles — fondations",
    categorie: "Gros œuvre > Terrassement",
    unite: "m³",
    chantiers: [
      {
        id: 3001,
        nom: "Résidence Les Tilleuls",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-02-25",
        fournisseur: "VRD Experts",
        quantite: "—",
        prixUnitaire: "39.50 €/m³",
        prixActualise: "39.50 €/m³",
        prixTotal: "—",
        source: "Devis non retenu",
        description: "Remblais des fouilles de fondations (estimation)"
      }
    ]
  },
  {
    id: 31,
    type: 'F+P',
    intitule: "Remblais des rigoles — réseaux",
    categorie: "Gros œuvre > Terrassement",
    unite: "m³",
    chantiers: [
      {
        id: 3101,
        nom: "Résidence Les Tilleuls",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-02-27",
        fournisseur: "VRD Experts",
        quantite: "19 m³",
        prixUnitaire: "47.20 €/m³",
        prixActualise: "47.20 €/m³",
        prixTotal: "897 €",
        source: "Devis retenu",
        description: "Remblais des rigoles après pose des réseaux"
      }
    ]
  },
  {
    id: 32,
    type: 'F+P',
    intitule: "Remblais talus entre plateformes",
    categorie: "Gros œuvre > Terrassement",
    unite: "m³",
    chantiers: [
      {
        id: 3201,
        nom: "Résidence Les Tilleuls",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-03-02",
        fournisseur: "VRD Experts",
        quantite: "764 m³",
        prixUnitaire: "35.30 €/m³",
        prixActualise: "35.30 €/m³",
        prixTotal: "26 969 €",
        source: "Devis retenu",
        description: "Remblais des talus entre plateformes (y compris compactage)"
      }
    ]
  },
  {
    id: 33,
    type: 'F+P',
    intitule: "Remblais pour reprise des plateformes",
    categorie: "Gros œuvre > Terrassement",
    unite: "m³",
    chantiers: [
      {
        id: 3301,
        nom: "Résidence Les Tilleuls",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-03-05",
        fournisseur: "VRD Experts",
        quantite: "—",
        prixUnitaire: "37.00 €/m³",
        prixActualise: "37.00 €/m³",
        prixTotal: "—",
        source: "Devis non retenu",
        description: "Remblais pour reprise et réglage des plateformes (estimation)"
      }
    ]
  },

  // ── Réseaux enterrés ──
  {
    id: 34,
    type: 'F+P',
    intitule: "Réseaux EP (eaux pluviales)",
    categorie: "Gros œuvre > Réseaux",
    unite: "ml",
    chantiers: [
      {
        id: 3401,
        nom: "Résidence Les Tilleuls — Zone 1",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-03-08",
        fournisseur: "VRD Experts",
        quantite: "10 ml",
        prixUnitaire: "30.68 €/ml",
        prixActualise: "30.68 €/ml",
        prixTotal: "307 €",
        source: "Devis retenu",
        description: "Réseau eaux pluviales — Zone 1"
      },
      {
        id: 3402,
        nom: "Résidence Les Tilleuls — Zone 2",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-03-10",
        fournisseur: "VRD Experts",
        quantite: "27 ml",
        prixUnitaire: "30.68 €/ml",
        prixActualise: "30.68 €/ml",
        prixTotal: "828 €",
        source: "Devis retenu",
        description: "Réseau eaux pluviales — Zone 2"
      },
      {
        id: 3403,
        nom: "Résidence Les Tilleuls — Zone 3",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-03-12",
        fournisseur: "VRD Experts",
        quantite: "18 ml",
        prixUnitaire: "30.68 €/ml",
        prixActualise: "30.68 €/ml",
        prixTotal: "552 €",
        source: "Devis retenu",
        description: "Réseau eaux pluviales — Zone 3"
      }
    ]
  },
  {
    id: 35,
    type: 'F+P',
    intitule: "Réseaux EU (eaux usées)",
    categorie: "Gros œuvre > Réseaux",
    unite: "ml",
    chantiers: [
      {
        id: 3501,
        nom: "Résidence Les Tilleuls — Zone 1",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-03-08",
        fournisseur: "VRD Experts",
        quantite: "173 ml",
        prixUnitaire: "30.68 €/ml",
        prixActualise: "30.68 €/ml",
        prixTotal: "5 308 €",
        source: "Devis retenu",
        description: "Réseau eaux usées — Zone 1"
      },
      {
        id: 3502,
        nom: "Résidence Les Tilleuls — Zone 2",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-03-10",
        fournisseur: "VRD Experts",
        quantite: "53 ml",
        prixUnitaire: "30.68 €/ml",
        prixActualise: "30.68 €/ml",
        prixTotal: "1 626 €",
        source: "Devis retenu",
        description: "Réseau eaux usées — Zone 2"
      },
      {
        id: 3503,
        nom: "Résidence Les Tilleuls — Zone 3",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-03-12",
        fournisseur: "VRD Experts",
        quantite: "84 ml",
        prixUnitaire: "30.68 €/ml",
        prixActualise: "30.68 €/ml",
        prixTotal: "2 577 €",
        source: "Devis retenu",
        description: "Réseau eaux usées — Zone 3"
      }
    ]
  },
  {
    id: 36,
    type: 'F+P',
    intitule: "Réseaux EV (eaux vannes)",
    categorie: "Gros œuvre > Réseaux",
    unite: "ml",
    chantiers: [
      {
        id: 3601,
        nom: "Résidence Les Tilleuls — Zone 1",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-03-08",
        fournisseur: "VRD Experts",
        quantite: "89 ml",
        prixUnitaire: "30.68 €/ml",
        prixActualise: "30.68 €/ml",
        prixTotal: "2 731 €",
        source: "Devis retenu",
        description: "Réseau eaux vannes — Zone 1"
      },
      {
        id: 3602,
        nom: "Résidence Les Tilleuls — Zone 2",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-03-10",
        fournisseur: "VRD Experts",
        quantite: "32 ml",
        prixUnitaire: "30.68 €/ml",
        prixActualise: "30.68 €/ml",
        prixTotal: "982 €",
        source: "Devis retenu",
        description: "Réseau eaux vannes — Zone 2"
      },
      {
        id: 3603,
        nom: "Résidence Les Tilleuls — Zone 3",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-03-12",
        fournisseur: "VRD Experts",
        quantite: "65 ml",
        prixUnitaire: "30.68 €/ml",
        prixActualise: "30.68 €/ml",
        prixTotal: "1 994 €",
        source: "Devis retenu",
        description: "Réseau eaux vannes — Zone 3"
      }
    ]
  },
  {
    id: 37,
    type: 'F+P',
    intitule: "Réseaux EG (eaux grasses)",
    categorie: "Gros œuvre > Réseaux",
    unite: "ml",
    chantiers: [
      {
        id: 3701,
        nom: "Résidence Les Tilleuls — Zone 1",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-03-08",
        fournisseur: "VRD Experts",
        quantite: "108 ml",
        prixUnitaire: "30.68 €/ml",
        prixActualise: "30.68 €/ml",
        prixTotal: "3 313 €",
        source: "Devis retenu",
        description: "Réseau eaux grasses — Zone 1"
      }
    ]
  },
  {
    id: 38,
    type: 'F+P',
    intitule: "Réseaux EU haute température",
    categorie: "Gros œuvre > Réseaux",
    unite: "ml",
    chantiers: [
      {
        id: 3801,
        nom: "Résidence Les Tilleuls — Zone 1",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-03-08",
        fournisseur: "VRD Experts",
        quantite: "37 ml",
        prixUnitaire: "102.63 €/ml",
        prixActualise: "102.63 €/ml",
        prixTotal: "3 797 €",
        source: "Devis retenu",
        description: "Réseau EU haute température (canalisation spéciale)"
      }
    ]
  },
  {
    id: 39,
    type: 'F+P',
    intitule: "Fourreaux électriques",
    categorie: "Gros œuvre > Réseaux",
    unite: "ml",
    chantiers: [
      {
        id: 3901,
        nom: "Résidence Les Tilleuls — Zone 1",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-03-08",
        fournisseur: "VRD Experts",
        quantite: "81 ml",
        prixUnitaire: "13.71 €/ml",
        prixActualise: "13.71 €/ml",
        prixTotal: "1 111 €",
        source: "Devis retenu",
        description: "Fourreaux électriques en tranchée — Zone 1"
      },
      {
        id: 3902,
        nom: "Résidence Les Tilleuls — Zone 2",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-03-10",
        fournisseur: "VRD Experts",
        quantite: "68 ml",
        prixUnitaire: "13.71 €/ml",
        prixActualise: "13.71 €/ml",
        prixTotal: "932 €",
        source: "Devis retenu",
        description: "Fourreaux électriques en tranchée — Zone 2"
      }
    ]
  },
  {
    id: 40,
    type: 'F+P',
    intitule: "Inspection caméra des réseaux enterrés",
    categorie: "Gros œuvre > Réseaux",
    unite: "U",
    chantiers: [
      {
        id: 4001,
        nom: "Résidence Les Tilleuls",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-04-15",
        fournisseur: "VRD Experts",
        quantite: "1 U",
        prixUnitaire: "2 000.00 €/U",
        prixActualise: "2 000.00 €/U",
        prixTotal: "2 000 €",
        source: "Devis retenu",
        description: "Inspection caméra de l'ensemble des réseaux enterrés"
      }
    ]
  },

  // ── Dallage PBR-1 (Plancher porté terre-plein) ──
  {
    id: 41,
    type: 'F+P',
    intitule: "PBR-1 — Plancher porté sur terre-plein, ép. 20 cm",
    categorie: "Gros œuvre > Dallage",
    unite: "m²",
    chantiers: [
      {
        id: 4101,
        nom: "Résidence Les Tilleuls",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-04-02",
        fournisseur: "Bâtiment Pro",
        quantite: "14 m²",
        prixUnitaire: "76.95 €/m²",
        prixActualise: "76.95 €/m²",
        prixTotal: "1 077 €",
        source: "Devis retenu",
        description: "Dallage porté sur terre-plein BA, ép. 20 cm"
      }
    ]
  },
  {
    id: 42,
    type: 'F+P',
    intitule: "PBR-1 — Plancher porté sur terre-plein, ép. 23 cm",
    categorie: "Gros œuvre > Dallage",
    unite: "m²",
    chantiers: [
      {
        id: 4201,
        nom: "Résidence Les Tilleuls",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-04-02",
        fournisseur: "Bâtiment Pro",
        quantite: "115 m²",
        prixUnitaire: "78.95 €/m²",
        prixActualise: "78.95 €/m²",
        prixTotal: "9 079 €",
        source: "Devis retenu",
        description: "Dallage porté sur terre-plein BA, ép. 23 cm"
      }
    ]
  },
  {
    id: 43,
    type: 'F+P',
    intitule: "PBR-1 — Plancher porté sur terre-plein, ép. 25 cm",
    categorie: "Gros œuvre > Dallage",
    unite: "m²",
    chantiers: [
      {
        id: 4301,
        nom: "Résidence Les Tilleuls",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-04-02",
        fournisseur: "Bâtiment Pro",
        quantite: "663 m²",
        prixUnitaire: "80.95 €/m²",
        prixActualise: "80.95 €/m²",
        prixTotal: "53 670 €",
        source: "Devis retenu",
        description: "Dallage porté sur terre-plein BA, ép. 25 cm"
      }
    ]
  },

  // ── Voiles béton armé ──
  // Les 4 voiles courants (niveaux 1 à 4) sont consolidés en un seul ouvrage
  // « Voiles béton armé » avec 4 chantiers ; le couronnement / acrotères reste
  // une entrée distincte (libellé et PU différents).
  {
    id: 44,
    type: 'F+P',
    intitule: "Voiles béton armé",
    categorie: "Gros œuvre > Voiles",
    unite: "m²",
    chantiers: [
      {
        id: 4401,
        nom: "Résidence Les Tilleuls — Niveau 1",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-05-10",
        fournisseur: "BTP Renard",
        quantite: "694 m²",
        prixUnitaire: "69.45 €/m²",
        prixActualise: "69.45 €/m²",
        prixTotal: "48 198 €",
        source: "Facture négociée",
        description: "Voiles béton armé courants — niveau 1"
      },
      {
        id: 4402,
        nom: "Résidence Les Tilleuls — Niveau 2",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-05-22",
        fournisseur: "BTP Renard",
        quantite: "1 276 m²",
        prixUnitaire: "84.83 €/m²",
        prixActualise: "84.83 €/m²",
        prixTotal: "108 243 €",
        source: "Facture négociée",
        description: "Voiles béton armé courants — niveau 2"
      },
      {
        id: 4403,
        nom: "Résidence Les Tilleuls — Niveau 3",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-06-05",
        fournisseur: "BTP Renard",
        quantite: "1 173 m²",
        prixUnitaire: "82.71 €/m²",
        prixActualise: "82.71 €/m²",
        prixTotal: "97 019 €",
        source: "Facture négociée",
        description: "Voiles béton armé courants — niveau 3"
      },
      {
        id: 4404,
        nom: "Résidence Les Tilleuls — Niveau 4",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-06-18",
        fournisseur: "BTP Renard",
        quantite: "1 026 m²",
        prixUnitaire: "81.07 €/m²",
        prixActualise: "81.07 €/m²",
        prixTotal: "83 178 €",
        source: "Facture négociée",
        description: "Voiles béton armé courants — niveau 4"
      }
    ]
  },
  {
    id: 45,
    type: 'F+P',
    intitule: "Voiles béton armé (couronnement / acrotères)",
    categorie: "Gros œuvre > Voiles",
    unite: "m²",
    chantiers: [
      {
        id: 4501,
        nom: "Résidence Les Tilleuls",
        localisation: "Reims",
        annee: "2025",
        dateChantier: "2025-07-02",
        fournisseur: "BTP Renard",
        quantite: "78 m²",
        prixUnitaire: "95.72 €/m²",
        prixActualise: "95.72 €/m²",
        prixTotal: "7 466 €",
        source: "Facture négociée",
        description: "Voiles béton armé couronnement / acrotères"
      }
    ]
  }
];

// Fonctions utilitaires pour la gestion des données
export const getOuvragesByCategory = (categorie: string): Ouvrage[] => {
  return allData.filter(ouvrage => ouvrage.categorie.toLowerCase().includes(categorie.toLowerCase()));
};

export const getOuvragesByUnite = (unite: string): Ouvrage[] => {
  return allData.filter(ouvrage => ouvrage.unite.toLowerCase() === unite.toLowerCase());
};

export const searchOuvrages = (query: string): Ouvrage[] => {
  const lowerQuery = query.toLowerCase();
  return allData.filter(ouvrage => 
    ouvrage.intitule.toLowerCase().includes(lowerQuery) ||
    ouvrage.categorie.toLowerCase().includes(lowerQuery)
  );
};

export const getOuvrageById = (id: number): Ouvrage | undefined => {
  return allData.find(ouvrage => ouvrage.id === id);
};

export const getChantiersByLocalisation = (localisation: string): Chantier[] => {
  const chantiers: Chantier[] = [];
  allData.forEach(ouvrage => {
    ouvrage.chantiers.forEach(chantier => {
      if (chantier.localisation.toLowerCase().includes(localisation.toLowerCase())) {
        chantiers.push(chantier);
      }
    });
  });
  return chantiers;
};

export const getChantiersByFournisseur = (fournisseur: string): Chantier[] => {
  const chantiers: Chantier[] = [];
  allData.forEach(ouvrage => {
    ouvrage.chantiers.forEach(chantier => {
      if (chantier.fournisseur.toLowerCase().includes(fournisseur.toLowerCase())) {
        chantiers.push(chantier);
      }
    });
  });
  return chantiers;
};

export const getChantiersByProjet = (projet: string): Chantier[] => {
  const chantiers: Chantier[] = [];
  allData.forEach(ouvrage => {
    ouvrage.chantiers.forEach(chantier => {
      if (chantier.nom.toLowerCase().includes(projet.toLowerCase())) {
        chantiers.push(chantier);
      }
    });
  });
  return chantiers;
};

export const getChantiersByAnnee = (annee: string): Chantier[] => {
  const chantiers: Chantier[] = [];
  allData.forEach(ouvrage => {
    ouvrage.chantiers.forEach(chantier => {
      if (chantier.annee === annee) {
        chantiers.push(chantier);
      }
    });
  });
  return chantiers;
};

export const getChantiersByPrixRange = (prixMin?: number, prixMax?: number): Chantier[] => {
  const chantiers: Chantier[] = [];
  allData.forEach(ouvrage => {
    ouvrage.chantiers.forEach(chantier => {
      const prix = parseFloat(chantier.prixUnitaire.replace(/[^\d,]/g, '').replace(',', '.'));
      const minPrix = prixMin || 0;
      const maxPrix = prixMax || Infinity;
      
      if (prix >= minPrix && prix <= maxPrix) {
        chantiers.push(chantier);
      }
    });
  });
  return chantiers;
};
